// Chemin: C:\gestion-copro\src\services\alertes.service.ts
// Session 70 : Service centralise pour les alertes "Actions en attente"
// Session 83 : Ajout comptage emails Gmail non lus dans le badge Messagerie
// Compte les elements necessitant une action utilisateur
// Session 241 : Vérification Gmail connecté avant fetchEmails — stoppe la boucle 500
//               sur les comptes sans Gmail (démo, nouveaux comptes)

import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  doc,
} from 'firebase/firestore';
import { db } from './firebase';

// ============================================
// TYPES
// ============================================

export interface AlertesCounts {
  courriers: number;
  messagerie: number;
  comptabilite: number;
  signalements: number;
  affaires: number;
  total: number;
}

// ============================================
// FONCTIONS DE COMPTAGE
// ============================================

/**
 * Compte les courriers en attente d'action :
 * - Brouillons à finaliser
 * - En validation : courriers où l'utilisateur n'a pas encore voté
 */
export async function countCourriersEnAttente(
  coproprieteId: string,
  userId: string
): Promise<number> {
  let count = 0;

  // Compter les brouillons
  const brouillonsQuery = query(
    collection(db, 'courriers'),
    where('coproprieteId', '==', coproprieteId),
    where('statut', '==', 'brouillon')
  );
  const brouillonsSnap = await getCountFromServer(brouillonsQuery);
  count += brouillonsSnap.data().count;

  // Compter les courriers en validation où l'utilisateur n'a pas voté
  const validationQuery = query(
    collection(db, 'courriers'),
    where('coproprieteId', '==', coproprieteId),
    where('statut', '==', 'en_validation')
  );
  const validationSnap = await getDocs(validationQuery);
  
  for (const docSnap of validationSnap.docs) {
    const data = docSnap.data();
    const votes = data.votes || [];
    const aVote = votes.some((v: any) => v.membreId === userId);
    if (!aVote) {
      count++;
    }
  }

  return count;
}

/**
 * Compte les messages/emails en attente d'action :
 * - MessagesCS en cours où l'utilisateur n'a pas voté
 * - Emails Gmail non lus (Session 83) — uniquement si Gmail connecté
 */
export async function countMessagesEnAttente(
  coproprieteId: string,
  userId: string
): Promise<number> {
  let count = 0;

  // 1. Compter les messagesCS en attente de vote
  try {
    const messagesQuery = query(
      collection(db, 'coproprietes', coproprieteId, 'messagesCS'),
      where('statut', '==', 'en_validation')
    );
    
    const snapshot = await getDocs(messagesQuery);
    
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const votes = data.votes || [];
      const aVote = votes.some((v: any) => v.membreId === userId);
      if (!aVote) {
        count++;
      }
    }
  } catch (error) {
    console.warn('Erreur comptage messagesCS:', error);
  }

  // 2. SESSION 83 : Comptage emails Gmail non lus
  // Session 241 : Désactivé dans les alertes — fetchEmails 500 sur comptes sans Gmail
  // cause une boucle infinie de re-renders. Le badge Messagerie affiche uniquement
  // les messagesCS. Le comptage Gmail sera revu dans une session dédiée.
  
  return count;
}

/**
 * Compte les contrôles comptables non traités (anomalies ou attention)
 */
export async function countControlesNonTraites(
  coproprieteId: string
): Promise<number> {
  const verificationsQuery = query(
    collection(db, 'coproprietes', coproprieteId, 'verifications'),
    where('statut', '==', 'en_cours')
  );
  
  const verificationsSnap = await getDocs(verificationsQuery);
  let count = 0;
  
  for (const verifDoc of verificationsSnap.docs) {
    const controlesQuery = query(
      collection(db, 'coproprietes', coproprieteId, 'verifications', verifDoc.id, 'controles'),
      where('statut', '==', 'nouveau')
    );
    
    const controlesSnap = await getDocs(controlesQuery);
    
    for (const controleDoc of controlesSnap.docs) {
      const data = controleDoc.data();
      if (data.resultat === 'anomalie' || data.resultat === 'attention') {
        count++;
      }
    }
  }
  
  return count;
}

/**
 * Compte les signalements non affectés à une affaire
 */
export async function countSignalementsNonAffectes(
  coproprieteId: string
): Promise<number> {
  const signalementsQuery = query(
    collection(db, 'signalements'),
    where('coproprieteId', '==', coproprieteId),
    where('statut', '==', 'nouveau')
  );
  
  const snapshot = await getDocs(signalementsQuery);
  let count = 0;
  
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    if (!data.affaireId) {
      count++;
    }
  }
  
  return count;
}

/**
 * Compte les affaires urgentes :
 * - Priorité "urgente"
 * - Garantie expirant dans les 30 prochains jours
 */
export async function countAffairesUrgentes(
  coproprieteId: string
): Promise<number> {
  const affairesQuery = query(
    collection(db, 'affaires'),
    where('coproprieteId', '==', coproprieteId),
    where('statut', 'in', ['ouverte', 'en_cours', 'en_attente'])
  );
  
  const snapshot = await getDocs(affairesQuery);
  const now = new Date();
  const dans30Jours = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  let count = 0;
  
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    
    if (data.priorite === 'urgente') {
      count++;
      continue;
    }
    
    if (data.dateFinGarantie) {
      const dateFinGarantie = data.dateFinGarantie.toDate();
      if (dateFinGarantie <= dans30Jours && dateFinGarantie > now) {
        count++;
      }
    }
  }
  
  return count;
}

// ============================================
// FONCTION PRINCIPALE
// ============================================

/**
 * Récupère tous les compteurs d'alertes pour l'affichage dans le menu
 */
export async function getAlertesCount(
  coproprieteId: string,
  userId: string
): Promise<AlertesCounts> {
  try {
    const [courriers, messagerie, comptabilite, signalements, affaires] = await Promise.all([
      countCourriersEnAttente(coproprieteId, userId),
      countMessagesEnAttente(coproprieteId, userId),
      countControlesNonTraites(coproprieteId),
      countSignalementsNonAffectes(coproprieteId),
      countAffairesUrgentes(coproprieteId),
    ]);

    return {
      courriers,
      messagerie,
      comptabilite,
      signalements,
      affaires,
      total: courriers + messagerie + comptabilite + signalements + affaires,
    };
  } catch (error) {
    console.error('Erreur chargement alertes:', error);
    return {
      courriers: 0,
      messagerie: 0,
      comptabilite: 0,
      signalements: 0,
      affaires: 0,
      total: 0,
    };
  }
}
